import { useSelector } from "react-redux";
import { canShowLoyaltyStanding } from "../program";
import { getMembershipStatus } from "../provider";
import {
  selectActiveLoyaltyProvider,
  selectCustomerLoyaltyInfo,
  selectLoyaltyProgramDetails,
} from "../selectors";

/**
 * What the current customer's standing with the active provider looks like — for the surfaces
 * that display it rather than redeem against it (the cart chip, the customer summary, the tier
 * badge on the customer form).
 *
 * Both answers are provider-declared, so a surface renders what it is given instead of asking
 * which partner is active:
 *
 * - `canShow` — is there anything worth showing? A provider that publishes a programme needs a
 *   live one; a provider without the concept always answers yes. That is the customer summary's
 *   rule, `canShowLoyaltyStanding`; the web cart's chip asks the narrower `canShowPointsStanding`.
 * - `membershipStatus` — the tier/membership label the provider exposes, or `undefined` when it
 *   has none. Only one partner has this today, and it is a field name in its profile, not a
 *   branch here.
 */
export function useLoyaltyStanding() {
  const activeProvider = useSelector(selectActiveLoyaltyProvider);
  const programDetails = useSelector(selectLoyaltyProgramDetails);
  const customerLoyaltyInfo = useSelector(selectCustomerLoyaltyInfo);

  return {
    activeProvider,
    programDetails,
    customerLoyaltyInfo,
    canShow: canShowLoyaltyStanding(activeProvider?.metaId, programDetails),
    membershipStatus: getMembershipStatus(activeProvider?.metaId, customerLoyaltyInfo),
  };
}
